/**
 * Apple Pencil detection utility for iPad drawing applications
 * Detects Apple Pencil vs finger touches and provides pressure/tilt data
 */

export interface PencilData {
  readonly isPencil: boolean;
  readonly pressure: number; // 0.0 to 1.0
  readonly tiltX: number; // -90 to 90 degrees
  readonly tiltY: number; // -90 to 90 degrees
  readonly twist: number; // 0 to 359 degrees (Pencil 2+)
  readonly width: number; // Contact area width
  readonly height: number; // Contact area height
  readonly pointerType: string; // "pen", "touch", "mouse"
}

export interface PencilDetectorConfig {
  readonly onPencilDetected?: (data: PencilData) => void;
  readonly onFingerDetected?: (data: PencilData) => void;
  readonly onPencilChange?: (isPencil: boolean) => void;
  readonly debug?: boolean;
}

export interface PencilDetectorAPI {
  readonly analyzeTouchEvent: (event: TouchEvent) => PencilData | null;
  readonly analyzePointerEvent: (event: PointerEvent) => PencilData;
  readonly getCurrentInputType: () => "pencil" | "finger" | "unknown";
  readonly destroy: () => void;
}

/**
 * Create Apple Pencil detector
 */
export const createPencilDetector = (
  config: PencilDetectorConfig = {}
): PencilDetectorAPI => {
  const {
    onPencilDetected,
    onFingerDetected,
    onPencilChange,
    debug = false,
  } = config;

  let isDestroyed = false;
  let currentInputType: "pencil" | "finger" | "unknown" = "unknown";
  let lastPencilState = false;

  /**
   * Analyze a pointer event for Apple Pencil characteristics
   */
  const analyzePointerEvent = (event: PointerEvent): PencilData => {
    const isPencil = event.pointerType === "pen";

    const pencilData: PencilData = {
      isPencil,
      pressure: event.pressure || 0,
      tiltX: event.tiltX || 0,
      tiltY: event.tiltY || 0,
      twist: event.twist || 0,
      width: event.width || 0,
      height: event.height || 0,
      pointerType: event.pointerType,
    };

    if (debug) {
      console.log("🖊️ Pointer analysis:", pencilData);
    }

    // Update current input type
    if (isPencil) {
      currentInputType = "pencil";
      onPencilDetected?.(pencilData);
    } else if (event.pointerType === "touch") {
      currentInputType = "finger";
      onFingerDetected?.(pencilData);
    }

    // Notify of pencil state change
    if (lastPencilState !== isPencil) {
      lastPencilState = isPencil;
      onPencilChange?.(isPencil);
    }

    return pencilData;
  };

  /**
   * Analyze a touch event for potential Apple Pencil characteristics
   * Less reliable than pointer events, but useful as fallback
   */
  const analyzeTouchEvent = (event: TouchEvent): PencilData | null => {
    if (event.touches.length === 0) return null;

    const touch = event.touches[0];

    // Heuristic analysis for Apple Pencil
    // Apple Pencil typically has:
    // - Smaller contact area (radiusX/Y < 10-15)
    // - More precise force values
    // - Different pressure characteristics
    const hasForce = touch.force !== undefined && touch.force > 0;
    const hasSmallContactArea =
      (touch.radiusX || 0) < 15 && (touch.radiusY || 0) < 15;
    const hasPreciseContact =
      touch.radiusX !== undefined && touch.radiusY !== undefined;

    // Educated guess based on available data
    const likelyPencil = hasForce && hasSmallContactArea && hasPreciseContact;

    const pencilData: PencilData = {
      isPencil: likelyPencil,
      pressure: touch.force || 0,
      tiltX: 0, // Not available in touch events
      tiltY: 0, // Not available in touch events
      twist: touch.rotationAngle || 0,
      width: touch.radiusX ? touch.radiusX * 2 : 0,
      height: touch.radiusY ? touch.radiusY * 2 : 0,
      pointerType: likelyPencil ? "pen" : "touch",
    };

    if (debug) {
      console.log("🖊️ Touch analysis (heuristic):", {
        ...pencilData,
        reasoning: {
          hasForce,
          hasSmallContactArea,
          hasPreciseContact,
          radiusX: touch.radiusX,
          radiusY: touch.radiusY,
          force: touch.force,
        },
      });
    }

    // Update current input type based on heuristic
    if (likelyPencil) {
      currentInputType = "pencil";
      onPencilDetected?.(pencilData);
    } else {
      currentInputType = "finger";
      onFingerDetected?.(pencilData);
    }

    // Notify of pencil state change
    if (lastPencilState !== likelyPencil) {
      lastPencilState = likelyPencil;
      onPencilChange?.(likelyPencil);
    }

    return pencilData;
  };

  /**
   * Get current input type
   */
  const getCurrentInputType = (): "pencil" | "finger" | "unknown" => {
    return currentInputType;
  };

  /**
   * Cleanup resources
   */
  const destroy = (): void => {
    isDestroyed = true;
    currentInputType = "unknown";
  };

  // Check for Pointer Events API support
  if (debug) {
    const hasPointerEvents = "PointerEvent" in window;
    const hasTouchForce = "ontouchforcechange" in window;

    console.log("🖊️ Browser support:", {
      pointerEvents: hasPointerEvents,
      touchForce: hasTouchForce,
      recommendation: hasPointerEvents
        ? "Use pointer events"
        : "Use touch event heuristics",
    });
  }

  return {
    analyzeTouchEvent,
    analyzePointerEvent,
    getCurrentInputType,
    destroy,
  };
};

/**
 * Check if current device supports Apple Pencil detection
 */
export const supportsPencilDetection = (): boolean => {
  return "PointerEvent" in window && /iPad|iPhone/.test(navigator.userAgent);
};

/**
 * Get recommended detection method for current device
 */
export const getRecommendedDetectionMethod = ():
  | "pointer"
  | "touch"
  | "none" => {
  if ("PointerEvent" in window) return "pointer";
  if ("ontouchstart" in window) return "touch";
  return "none";
};

